import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export const EMPTY_CONTEST = {
  name: "",
  description: "",
  start_datetime: "",
  end_datetime: "",
  win_probability: 0.5,
  unique_code_enabled: true,
  loyalty_card_enabled: false,
  loyalty_card_prefix: "",
  logo_url: "",
  logo_file_id: null,
  store_ids: null,
  initial_batches: [],
  printer_mode: "browser",
  printer_ip: "",
  printer_port: 9100,
  printer_width: 80,
  auto_print: false,
  receipt_expiry_days: "",
  reminder_days_before: 3,
  channel: "totem",
  receipt_ocr_enabled: false,
  web_enabled: false,
  web_daily_limit: 0,
  totem_enabled: true,
  whatsapp_enabled: false,
  partner_enabled: false,
  loyalty_config: { format: "any", mode: "prefix", prefix: "", length: "", api_url: "", api_key_header: "X-Api-Key", api_key: "", alert_threshold: 5 },
  access_mode: "free",
  whatsapp_number: "",
  marketing_consent_text: "",
  rules_text: "",
  rules_file_id: null,
  privacy_text: "",
  privacy_file_id: null,
  contact_email: "",
  contact_phone: "",
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const contestToForm = (contest) => ({
  ...EMPTY_CONTEST,
  ...contest,
  description: contest.description || "",
  loyalty_card_prefix: contest.loyalty_card_prefix || "",
  logo_url: contest.logo_url || "",
  logo_file_id: contest.logo_file_id || null,
  printer_mode: contest.printer_mode || "browser",
  printer_ip: contest.printer_ip || "",
  printer_port: contest.printer_port || 9100,
  printer_width: contest.printer_width || 80,
  auto_print: !!contest.auto_print,
  receipt_expiry_days: contest.receipt_expiry_days ?? "",
  reminder_days_before: contest.reminder_days_before ?? 3,
  channel: contest.channel || "totem",
  web_enabled: !!contest.web_enabled,
  web_daily_limit: contest.web_daily_limit || 0,
  totem_enabled: contest.totem_enabled !== false,
  whatsapp_enabled: !!contest.whatsapp_enabled || contest.channel === "whatsapp",
  partner_enabled: !!contest.partner_enabled,
  loyalty_config: { format: "any", mode: "prefix", prefix: contest.loyalty_card_prefix || "", length: "", api_url: "", api_key_header: "X-Api-Key", api_key: "", alert_threshold: 5, ...(contest.loyalty_config || {}) },
  access_mode: contest.access_mode || "free",
  whatsapp_number: contest.whatsapp_number || "",
  marketing_consent_text: contest.marketing_consent_text || "",
  rules_text: contest.rules_text || "",
  rules_file_id: contest.rules_file_id || null,
  privacy_text: contest.privacy_text || "",
  privacy_file_id: contest.privacy_file_id || null,
  contact_email: contest.contact_email || "",
  contact_phone: contest.contact_phone || "",
  start_datetime: toLocalInput(contest.start_datetime),
  end_datetime: toLocalInput(contest.end_datetime),
});

export const validateContestForm = (form) => {
  if (!form.name || !form.start_datetime || !form.end_datetime) return "Compila nome, data inizio e data fine";
  if (new Date(form.end_datetime) <= new Date(form.start_datetime)) return "La data di fine deve essere successiva a quella di inizio";
  return null;
};

export const formToPayload = (form) => ({
  name: form.name,
  description: form.description || null,
  start_datetime: new Date(form.start_datetime).toISOString(),
  end_datetime: new Date(form.end_datetime).toISOString(),
  win_probability: form.win_probability,
  unique_code_enabled: form.unique_code_enabled,
  loyalty_card_enabled: form.loyalty_card_enabled,
  loyalty_card_prefix: form.loyalty_card_prefix || null,
  logo_url: form.logo_url || null,
  logo_file_id: form.logo_file_id || null,
  store_ids: form.store_ids ?? null,
  initial_batches: form.initial_batches?.length ? form.initial_batches.map((b) => ({ store_id: b.store_id || null, prefix: b.prefix || "", label: b.label || null, quantity: Math.max(1, Math.min(5000, Number(b.quantity) || 100)) })) : null,
  printer_mode: form.printer_mode,
  printer_ip: form.printer_ip || null,
  printer_port: Number(form.printer_port) || 9100,
  printer_width: Number(form.printer_width) === 58 ? 58 : 80,
  auto_print: !!form.auto_print,
  receipt_expiry_days: form.receipt_expiry_days === "" ? null : Math.max(1, Number(form.receipt_expiry_days) || 0) || null,
  reminder_days_before: Math.max(1, Number(form.reminder_days_before) || 3),
  channel: form.channel || "totem",
  receipt_ocr_enabled: !!form.receipt_ocr_enabled,
  web_enabled: !!form.web_enabled,
  web_daily_limit: Number(form.web_daily_limit) || 0,
  totem_enabled: !!form.totem_enabled,
  whatsapp_enabled: !!form.whatsapp_enabled,
  partner_enabled: !!form.partner_enabled,
  loyalty_config: form.loyalty_config ? { ...form.loyalty_config, length: Number(form.loyalty_config.length) || null } : null,
  loyalty_card_prefix: form.loyalty_config?.prefix || form.loyalty_card_prefix || "",
  channel: form.whatsapp_enabled && !form.totem_enabled ? "whatsapp" : "totem",
  access_mode: form.access_mode || "free",
  whatsapp_number: form.whatsapp_number?.trim() || null,
  marketing_consent_text: form.marketing_consent_text?.trim() || null,
  rules_text: form.rules_text?.trim() || null,
  rules_file_id: form.rules_file_id || null,
  privacy_text: form.privacy_text?.trim() || null,
  privacy_file_id: form.privacy_file_id || null,
  contact_email: form.contact_email?.trim() || null,
  contact_phone: form.contact_phone?.trim() || null,
});

export function useContestForm({ open, contest, onOpenChange, onCreated }) {
  const isEdit = !!contest;
  const [form, setForm] = useState(EMPTY_CONTEST);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const merge = (v) => setForm((f) => ({ ...f, ...v }));

  useEffect(() => {
    if (!open) return;
    setForm(contest ? contestToForm(contest) : EMPTY_CONTEST);
  }, [open, contest]);

  const submit = async () => {
    const error = validateContestForm(form);
    if (error) return toast.error(error);
    setSaving(true);
    try {
      if (isEdit) {
        await api.updateContest(contest.id, formToPayload(form));
        toast.success("Concorso aggiornato");
      } else {
        const created = await api.createContest(formToPayload(form));
        toast.success(form.receipt_ocr_enabled ? "Concorso creato: lettura scontrini attiva, completa le regole" : "Concorso creato");
        onOpenChange(false);
        onCreated?.(created);
        return;
      }
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(isEdit ? "Errore nell'aggiornamento del concorso" : "Errore nella creazione del concorso");
    } finally {
      setSaving(false);
    }
  };

  return { form, set, merge, submit, saving, isEdit };
}
